/**
 * Root application component that applies the global Material-UI theme
 * and renders the route configuration alongside the site footer.
 *
 * @module App
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import {createTheme} from '@material-ui/core/styles';
import {MuiThemeProvider} from '@material-ui/core';
import Common from './routes/common';
import Footer from './components/common/Footer.jsx';

/**
 * A global theme configuration object used to define the primary visual styles of the application.
 * This theme is created using `createTheme` and specifies color palettes and typography settings.
 *
 * Properties:
 * - palette: Contains color definitions for the primary and secondary themes, including main, light, dark, and contrastText variations.
 * - typography: Configures the typography settings, including the global font family.
 *
 * The `globalTheme` is designed to ensure consistent styling throughout the application.
 */
const globalTheme = createTheme({
    palette: {
        primary: {
            main: 'rgb(136,38,71)',
            light: '#bb5672',
            dark: '#560020',
            contrastText: '#fff',
        },
        secondary: {
            main: '#f8dee7',
            light: '#ffffff',
            dark: '#c5acb5',
            contrastText: '#000000',
        },
    },
    typography: {
        fontFamily: 'Proxima Nova',
    },
});

/**
 * A functional React component that serves as the root application layout.
 * It provides a flex column layout with a minimum height of 100% of the viewport height.
 * The component includes a global theme provider and renders common content followed by a footer.
 *
 * @return {JSX.Element} The rendered JSX structure for the App component.
 */
function App() {
    return (
        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '100vh'}}>
            <MuiThemeProvider theme={globalTheme}>
                <Common/>
                <Footer/>
            </MuiThemeProvider>
        </div>
    );
}

export default App;
